<template>
  <Dropzone v-slot="{ active }" class="z-0" @files="addFiles">
    <StripedBox
      border-color="var(--color-element-neutral-100)"
      border-radius="4px"
      class="box cursor-pointer"
      :class="{ active }"
      @click.prevent.stop="emit('click')"
    >
      <div class="drop-box">
        <StripedBox
          border-color="var(--color-element-neutral-100)"
          border-radius="4px"
          :aspect="1"
          :active-box="!value"
          use-height
        >
          <Box v-if="value" :aspect="1" use-height>
            <inline-file :file="value" @file="hasFile = !!$event" @remove="removeFile" />
          </Box>
          <Box
            v-else
            :aspect="1"
            class="placeholder"
            use-height
          >
            <PhotoIcon class="m-auto w-7.5 h-7.5" />
          </Box>
        </StripedBox>

        <slot />
      </div>
    </StripedBox>
  </Dropzone>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import PhotoIcon from '@images/photo.svg?component';
import { useVModel } from '@vueuse/core';
import InlineFile from '@/components/utils/upload/InlineFile.vue';
import Box from '../Box.vue';
import Dropzone from '../Dropzone.vue';
import type { WbFile } from '@/entities/Entities';
import StripedBox from '../StripedBox.vue';
import { Upload, type FileUpload } from '../upload/fileUpload';

interface Props {
  alt?: string;
  acceptedFiles?: string;
  useHeight?: boolean;
  modelValue?: FileUpload<WbFile>;
}

const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue', 'click']);
const value = useVModel(props, 'modelValue', emit, { passive: true });

const hasFile = ref<boolean>(false);

const addFiles = (files: File[]) => {
  if (!files.length) return;
  const upload = Upload.from<WbFile>(files[0]);
  value.value = upload;
};

const removeFile = () => {
  value.value = undefined;
  hasFile.value = false;
};
</script>

<style lang="scss" scoped>
.close-icon {
  // @apply rounded-full border-2 border-white;
  @apply absolute w-9 h-9 top-0 right-0 text-white;
  @apply cursor-pointer;

  stroke-width: px(4);
  padding: px(5);

  svg {
    @apply w-full h-full;
  }
}

.progress {
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2;
  @apply w-9/12;
}

.placeholder :deep(.box-content) {
  @apply flex justify-center items-center h-full pointer-events-none;
}

:deep(.box.active) {
  --stripe-box-border-color: var(--color-text-dark-100);
}
</style>
